import React, { useState, useMemo, useEffect } from 'react';
import { Search, Filter, HelpCircle, FileText, Download, CheckCircle, BarChart3, Layers } from 'lucide-react';

import { MAPPING_DATA, CATEGORIES, ComplianceItem, ComplianceChecklist, ExpandedItems, CategoryStats } from '../data/complianceData';
import GridView from './GridView';
import ChecklistView from './ChecklistView';
import VisualizationView from './VisualizationView';
import DetailModal from './DetailModal';
import HowItWorksModal from './HowItWorksModal';
import ToolsShowcase from './ToolsShowcase';
import WelcomeModal, { hasBeenWelcomed } from './WelcomeModal';
import { Shield, Globe } from 'lucide-react';

const CybersecurityMapper: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedItem, setSelectedItem] = useState<ComplianceItem | null>(null);
  const [expandedItems, setExpandedItems] = useState<ExpandedItems>({});
  const [showExportOptions, setShowExportOptions] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<string>('grid');
  const [checklist, setChecklist] = useState<ComplianceChecklist>({});
  const [selectedRequirements, setSelectedRequirements] = useState<Set<string>>(new Set());
  const [showHowItWorksModal, setShowHowItWorksModal] = useState<boolean>(false);
  const [showWelcome, setShowWelcome] = useState<boolean>(!hasBeenWelcomed());

  useEffect(() => {
    const saved = localStorage.getItem('complianceChecklist');
    if (saved) setChecklist(JSON.parse(saved));
  }, []);

  useEffect(() => {
    if (Object.keys(checklist).length > 0) {
      localStorage.setItem('complianceChecklist', JSON.stringify(checklist));
    }
  }, [checklist]);

  const filteredData = useMemo(() => {
    return MAPPING_DATA.filter(item => {
      const lower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || (
        item.title.toLowerCase().includes(lower) ||
        item.id.includes(searchTerm) ||
        item.iso27001.toLowerCase().includes(lower) ||
        item.nist.toLowerCase().includes(lower) ||
        item.etsi.toLowerCase().includes(lower) ||
        item.cen.toLowerCase().includes(lower) ||
        Object.values(item.national).some(v => v.toLowerCase().includes(lower))
      );
      const matchesCategory = selectedCategory === 'all' ||
        Object.entries(CATEGORIES).some(([cat, ids]) => cat === selectedCategory && ids.includes(item.id));
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  const complianceStats = useMemo(() => {
    const total = MAPPING_DATA.length;
    const completed = Object.values(checklist).filter(s => s === 'completed').length;
    const inProgress = Object.values(checklist).filter(s => s === 'in-progress').length;
    return { total, completed, inProgress, notStarted: total - completed - inProgress, completionRate: total > 0 ? Math.round((completed / total) * 100) : 0 };
  }, [checklist]);

  const categoryStats = useMemo<CategoryStats>(() => {
    const stats: CategoryStats = {};
    Object.entries(CATEGORIES).forEach(([category, ids]) => {
      const items = ids.filter(id => MAPPING_DATA.some(d => d.id === id));
      const completed = items.filter(id => checklist[id] === 'completed').length;
      stats[category] = { total: items.length, completed, percentage: items.length > 0 ? Math.round((completed / items.length) * 100) : 0 };
    });
    return stats;
  }, [checklist]);

  const toggleExpand = (id: string) => setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));

  const toggleRequirement = (id: string) => {
    const next = new Set(selectedRequirements);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedRequirements(next);
  };

  const updateStatus = (id: string, status: string) => setChecklist(prev => ({ ...prev, [id]: status }));

  const exportToCSV = () => {
    const headers = ['ID', 'Title', 'ISO 27001', 'NIST', 'ETSI', 'CEN', 'BE', 'FI', 'EL', 'ES', 'FR'];
    const rows = filteredData.map(item => [
      item.id, item.title, item.iso27001, item.nist, item.etsi, item.cen,
      item.national.BE, item.national.FI, item.national.EL, item.national.ES, item.national.FR,
    ]);
    const csv = [headers, ...rows].map(row => row.map(c => `"${c}"`).join(',')).join('\n');
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })), download: 'cybersecurity-mappings.csv' });
    a.click();
  };

  const exportToJSON = () => {
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([JSON.stringify(filteredData, null, 2)], { type: 'application/json' })), download: 'cybersecurity-mappings.json' });
    a.click();
  };

  return (
    <div className="cs-mapper">
      {/* Header */}
      <div className="cs-mapper-header">
        <h1 className="cs-title">Cybersecurity Standards Mapping Tool</h1>
        <p className="cs-subtitle">
          Navigate and search through cybersecurity framework mappings across ISO 27001, NIST, ETSI, and national standards
        </p>

        <div className="cs-guide">
          <div className="flex items-center justify-between mb-2">
            <h3 className="cs-guide-title">📘 Quick Guide</h3>
            <button
              onClick={() => setShowHowItWorksModal(true)}
              className="flex items-center gap-1 text-blue-200 hover:text-white text-sm transition-colors"
            >
              <HelpCircle size={16} />
              How It Works
            </button>
          </div>
          <ul className="cs-guide-list">
            <li className="cs-guide-item">• <strong>Search:</strong> Find requirements by ID, title, or any standard reference</li>
            <li className="cs-guide-item">• <strong>Filter by Category:</strong> Browse requirements by domain (Risk Management, Access Control, etc.)</li>
            <li className="cs-guide-item">• <strong>Export:</strong> Download filtered results as CSV or JSON for further analysis</li>
            <li className="cs-guide-item">• <strong>Requirement Levels:</strong> <span className="cs-badge cs-badge-green">BASIC</span> <span className="cs-badge cs-badge-yellow">IMPORTANT</span> <span className="cs-badge cs-badge-red">ESSENTIAL</span></li>
          </ul>
        </div>
      </div>

      {/* Controls */}
      <div className="cs-controls">
        <div className="cs-search-box">
          <Search className="cs-search-icon" size={18} />
          <input
            type="text"
            placeholder="Search by ID, title, or standard reference..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="cs-search-input"
          />
        </div>

        <div className="cs-filter-section">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="cs-filter-dropdown pl-10"
            >
              <option value="all">All Categories</option>
              {Object.keys(CATEGORIES).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            {[
              { mode: 'grid', icon: <Layers size={18} />, title: 'Grid View' },
              { mode: 'checklist', icon: <CheckCircle size={18} />, title: 'Compliance Checklist' },
              { mode: 'visualization', icon: <BarChart3 size={18} />, title: 'Visualization' },
            ].map(({ mode, icon, title }) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`p-2 rounded-lg transition-colors ${viewMode === mode ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                title={title}
              >
                {icon}
              </button>
            ))}
          </div>

          <div className="relative ml-auto flex gap-2">
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
            >
              <FileText size={16} />
              Export
            </button>
            <div className="relative">
              <button
                onClick={() => setShowExportOptions(!showExportOptions)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
              >
                <Download size={16} />
                Download
              </button>
              {showExportOptions && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <button onClick={() => { exportToCSV(); setShowExportOptions(false); }} className="w-full px-4 py-2 text-left hover:bg-gray-100 transition-colors">Export as CSV</button>
                  <button onClick={() => { exportToJSON(); setShowExportOptions(false); }} className="w-full px-4 py-2 text-left hover:bg-gray-100 transition-colors">Export as JSON</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-5 gap-4 mt-4 text-center">
          {[
            { value: filteredData.length, label: 'Requirements Found', color: 'text-blue-600' },
            { value: complianceStats.completed, label: 'Completed', color: 'text-green-600' },
            { value: complianceStats.inProgress, label: 'In Progress', color: 'text-yellow-600' },
            { value: complianceStats.notStarted, label: 'Not Started', color: 'text-gray-600' },
            { value: `${complianceStats.completionRate}%`, label: 'Completion Rate', color: 'text-purple-600' },
          ].map(({ value, label, color }) => (
            <div key={label} className="p-3">
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
              <p className="text-sm text-gray-600">{label}</p>
            </div>
          ))}
        </div>

        {selectedRequirements.size > 0 && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
            <span className="text-sm text-blue-800">{selectedRequirements.size} requirements selected for comparison</span>
            <button
              onClick={() => setSelectedRequirements(new Set())}
              className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Views */}
      {viewMode === 'grid' && (
        <>
          <GridView
            filteredData={filteredData}
            expandedItems={expandedItems}
            onToggleExpand={toggleExpand}
            selectedRequirements={selectedRequirements}
            onToggleRequirement={toggleRequirement}
            checklist={checklist}
            onStatusChange={updateStatus}
            onSelectItem={setSelectedItem}
          />
          {filteredData.length === 0 && (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <p className="text-gray-500">No results found for "{searchTerm}"</p>
            </div>
          )}
        </>
      )}

      {viewMode === 'checklist' && (
        <ChecklistView
          categories={CATEGORIES}
          filteredData={filteredData}
          categoryStats={categoryStats}
          expandedItems={expandedItems}
          onToggleExpand={toggleExpand}
          checklist={checklist}
          onStatusChange={updateStatus}
          onSelectItem={setSelectedItem}
        />
      )}

      {viewMode === 'visualization' && (
        <VisualizationView complianceStats={complianceStats} categoryStats={categoryStats} checklist={checklist} />
      )}

      <ToolsShowcase />

      {/* Footer */}
      <footer className="mt-12 pt-8 pb-10 border-t border-gray-200">
        <div className="text-center">
          <p className="text-gray-600 mb-6">© {new Date().getFullYear()} Compliance Mapping Tool. All rights reserved.</p>
          <div className="flex flex-wrap justify-center gap-6 max-w-3xl mx-auto">
            {[
              { href: 'https://cisomarketplace.com/', icon: <Shield className="w-4 h-4" />, label: 'CISO Marketplace' },
              { href: 'https://cisomarketplace.services/', icon: <Globe className="w-4 h-4" />, label: 'CISO Services' },
              { href: 'https://cyberciso.support/', icon: <Shield className="w-4 h-4" />, label: 'Cyber CISO' },
              { href: 'https://cyberadx.network/', icon: <Globe className="w-4 h-4" />, label: 'CyberAdX' },
            ].map(({ href, icon, label }) => (
              <a key={href} href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center gap-2">
                {icon}
                {label}
              </a>
            ))}
          </div>
        </div>
      </footer>

      {/* Modals */}
      {selectedItem && (
        <DetailModal
          item={selectedItem}
          checklist={checklist}
          onStatusChange={updateStatus}
          onClose={() => setSelectedItem(null)}
        />
      )}
      {showHowItWorksModal && (
        <HowItWorksModal onClose={() => setShowHowItWorksModal(false)} />
      )}
      {showWelcome && (
        <WelcomeModal onClose={() => setShowWelcome(false)} />
      )}
    </div>
  );
};

export default CybersecurityMapper;
